import { z } from 'zod';

// Invoice Analysis Types
export const invoiceItemSchema = z.object({
  lineNumber: z.number(),
  productName: z.string(),
  sku: z.string().nullable(),
  quantity: z.number(),
  unit: z.string(),
  unitPrice: z.number().nullable(),
  totalPrice: z.number().nullable(),
  lotNumber: z.string().nullable(),
  expiryDate: z.string().nullable(),
  confidence: z.number().min(0).max(1),
});

export const invoiceAnalysisResultSchema = z.object({
  documentType: z.enum(['invoice', 'packing-slip', 'delivery-note', 'unknown']),
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  supplierName: z.string().nullable(),
  items: z.array(invoiceItemSchema),
  subtotal: z.number().nullable(),
  tax: z.number().nullable(),
  total: z.number().nullable(),
  currency: z.string().nullable(),
  notes: z.string().nullable(),
});

export type InvoiceItem = z.infer<typeof invoiceItemSchema>;
export type InvoiceAnalysisResult = z.infer<typeof invoiceAnalysisResultSchema>;

// Box Counting Types
export const boxCountItemSchema = z.object({
  itemType: z.string(),
  description: z.string(),
  count: z.number(),
  confidence: z.number().min(0).max(1),
});

export const boxCountObservationsSchema = z.object({
  condition: z.enum(['good', 'damaged', 'mixed', 'unknown']),
  stacking: z.enum(['proper', 'improper', 'unknown']),
  labeling: z.enum(['visible', 'partial', 'none', 'unknown']),
  temperature: z.enum(['ambient', 'refrigerated', 'frozen', 'unknown']),
});

export const boxCountResultSchema = z.object({
  counts: z.array(boxCountItemSchema),
  totalBoxes: z.number(),
  totalPallets: z.number(),
  observations: boxCountObservationsSchema,
  visibleLabels: z.array(z.string()),
  concerns: z.array(z.string()),
  notes: z.string().nullable(),
});

export type BoxCountItem = z.infer<typeof boxCountItemSchema>;
export type BoxCountObservations = z.infer<typeof boxCountObservationsSchema>;
export type BoxCountResult = z.infer<typeof boxCountResultSchema>;

// Product Verification Types
export const productWeightSchema = z.object({
  value: z.number().nullable(),
  unit: z.string().nullable(),
});

export const productVerificationResultSchema = z.object({
  productName: z.string(),
  brand: z.string().nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  lotNumber: z.string().nullable(),
  batchCode: z.string().nullable(),
  productionDate: z.string().nullable(),
  expiryDate: z.string().nullable(),
  bestByDate: z.string().nullable(),
  useByDate: z.string().nullable(),
  weight: productWeightSchema,
  storageInstructions: z.string().nullable(),
  allergens: z.array(z.string()),
  countryOfOrigin: z.string().nullable(),
  certifications: z.array(z.string()),
  confidence: z.number().min(0).max(1),
  notes: z.string().nullable(),
});

export type ProductWeight = z.infer<typeof productWeightSchema>;
export type ProductVerificationResult = z.infer<typeof productVerificationResultSchema>;

// Analysis Mode
export type AnalysisMode = 'invoice' | 'box-counting' | 'product-verification';

// Matched Line Item (for mapping AI results to delivery lines)
export interface MatchedLineItem {
  deliveryLineId: string;
  invoiceItem: InvoiceItem;
  matchConfidence: number;
  matchReason: 'sku' | 'name' | 'manual';
  suggestedQuantity: number;
  suggestedLotNumber: string | null;
  suggestedExpiryDate: string | null;
}

// Image Queue Types
export type ImageQueueStatus = 'pending' | 'analyzing' | 'complete' | 'error';

export interface QueuedImage {
  id: string;
  base64: string;
  mediaType: 'image/jpeg' | 'image/png';
  mode: AnalysisMode;
  status: ImageQueueStatus;
  result: InvoiceAnalysisResult | BoxCountResult | ProductVerificationResult | null;
  error: string | null;
  timestamp: number;
}

// Consolidated Report (combines multiple image analyses)
export interface ConsolidatedReceivingReport {
  totalImages: number;
  processedImages: number;
  boxCounting: {
    totalBoxes: number;
    totalPallets: number;
    allCounts: BoxCountItem[];
    concerns: string[];
    observations: BoxCountObservations | null;
  };
  invoiceData: {
    items: InvoiceItem[];
    invoiceNumbers: string[];
    supplierNames: string[];
    totals: {
      subtotal: number;
      tax: number;
      total: number;
    };
  };
  productVerifications: ProductVerificationResult[];
  generatedAt: string;
}
